/**
 * MessageItem - Component for rendering a single chat message
 *
 * Extracted from ChatPanel.tsx to reduce complexity
 * Shows different detail views based on user role:
 * - Regular users: Only timestamp and references
 * - Admins/Developers: Full technical details (tokens, costs, flow, etc.)
 */

import React, { useState } from 'react';
import { BarChart2, Zap } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import MessageActions from '../MessageActions';
import UsageStats from '../UsageStats';
import MarkdownRenderer from '../MarkdownRenderer';
import MessageContextSection from './MessageContextSection';
import MentionChip from './MentionChip';
import ChatReferences from './ChatReferences';

import { detectMentions } from '../../utils/mentionUtils';
import { remarkCitations } from '../../utils/remarkCitations';
import type { ChatMessage, CitationData } from './types/chat.types';
import type { MinimalDocument } from '../../types';

export interface MessageItemProps {
  message: ChatMessage;
  expandedReferences: Set<string>;
  expandedContext: Set<string>;
  expandedUsageStats: Set<string>;
  onToggleExpansion: (type: 'refs' | 'context' | 'stats', id: string) => void;
  onCopy: (content: string) => void;
  onSave: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete: (id: string) => void;
  onRate?: (id: string, rating: 1 | 5, feedback?: string) => void;
  onRegenerate?: (id: string) => void;
  onCitationClick?: (citation: CitationData) => void;
  onChunkClick?: (citation: CitationData) => void;
  onStudioResultOpen?: (result: any) => void;
  availableDocuments: MinimalDocument[];
  selectedMinimalDocuments: MinimalDocument[] | null;
  createCitationComponents: (message: ChatMessage) => any;
  isAdmin?: boolean;
}

function areMessageItemPropsEqual(
  prevProps: MessageItemProps,
  nextProps: MessageItemProps
): boolean {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.text === nextProps.message.text &&
    prevProps.message.markdown === nextProps.message.markdown &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.cancelled === nextProps.message.cancelled &&
    prevProps.message.confidence === nextProps.message.confidence &&
    prevProps.message.excluded_from_context === nextProps.message.excluded_from_context &&
    prevProps.message.user_rating === nextProps.message.user_rating &&
    prevProps.message.is_saved === nextProps.message.is_saved &&
    prevProps.message.autoSelectionInfo === nextProps.message.autoSelectionInfo &&
    prevProps.expandedReferences === nextProps.expandedReferences &&
    prevProps.expandedContext === nextProps.expandedContext &&
    prevProps.expandedUsageStats === nextProps.expandedUsageStats &&
    prevProps.isAdmin === nextProps.isAdmin
  );
}

export const MessageItem = React.memo(function MessageItem({
  message,
  expandedReferences,
  expandedContext,
  expandedUsageStats,
  onToggleExpansion,
  onCopy,
  onSave,
  onEdit,
  onDelete,
  onRate,
  onRegenerate,
  onCitationClick,
  onChunkClick,
  onStudioResultOpen,
  availableDocuments,
  selectedMinimalDocuments,
  createCitationComponents,
  isAdmin,
}: MessageItemProps) {
  const { theme } = useTheme();

  const isUserMessage = message.isUser;
  const hasContext = message.selectedDocuments && message.selectedDocuments.length > 0;

  return (
    <div className="group/msg relative" style={{ maxWidth: isUserMessage ? '75%' : '850px' }}>
      {/* Bubble / Content area */}
      <div
        className={`transition-all ${
          message.excluded_from_context
            ? `opacity-50 border border-dashed ${theme.colors.border.primary} rounded-xl`
            : ''
        } ${
          isUserMessage
            ? `bg-nb-surface-hover ${theme.colors.text.primary} rounded-2xl`
            : `bg-transparent ${theme.colors.text.primary}`
        }`}
      >
        {/* Excluded Badge */}
        {message.excluded_from_context && (
          <div className="px-5 pt-3 pb-2">
            <span
              className={`inline-block px-2 py-0.5 text-xs rounded-full border ${theme.colors.border.primary} ${theme.colors.text.secondary}`}
            >
              Excluido del contexto
            </span>
          </div>
        )}

        <div className={`px-4 py-3 ${isUserMessage ? 'rounded-2xl' : ''}`}>
          {/* Studio Prompt Text */}
          {message.studioPrompt && (
            <div className="text-sm font-medium mb-3 opacity-80">{message.studioPrompt}</div>
          )}

          {/* Message Content */}
          <div
            className={`whitespace-pre-wrap leading-relaxed relative ${
              isUserMessage ? 'text-base font-sans' : 'text-base font-serif'
            }`}
          >
            {message.isUser ? renderUserMessage() : renderAIMessage()}
            {/* Blinking cursor while streaming — indicates text is still growing */}
            {message.isStreaming && !message.isUser && (
              <span className="inline-block w-[2px] h-[1em] bg-nb-text ml-0.5 align-middle animate-blink-cursor" />
            )}
            {message.isStreaming && (
              <div
                className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none animate-shimmer rounded-b-lg"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, transparent 25%, rgb(var(--nb-accent-rgb) / 0.08) 50%, transparent 75%)',
                  backgroundSize: '200% 100%',
                }}
              />
            )}
          </div>

          {/* Cancelled badge */}
          {message.cancelled && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs rounded-full bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20">
              Respuesta interrumpida
            </span>
          )}

          {/* Studio Result Button */}
          {message.studioResult && onStudioResultOpen && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => onStudioResultOpen(message.studioResult!)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${theme.colors.border.primary} text-xs font-medium ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
              >
                Ver en Drive
              </button>
            </div>
          )}

          {/* AI messages: unified meta-line (metadata left + actions right on hover) */}
          {!isUserMessage && renderAIFooter()}
        </div>
      </div>

      {/* User messages: metadata OUTSIDE bubble */}
      {isUserMessage && (
        <>
          {/* Context expansion (docs selected) - always visible when present */}
          {hasContext && expandedContext.has(message.id) && (
            <div className="mt-1 pr-1">
              <MessageContextSection
                selectedDocuments={message.selectedDocuments}
                availableDocuments={availableDocuments}
                messageText={message.text}
                citations={message.citations}
                isUserMessage={isUserMessage}
                onDocumentClick={(documentId) => {
                  if (onCitationClick) {
                    const doc = availableDocuments.find((d) => d.id === documentId);
                    if (doc) {
                      onCitationClick({
                        document_id: doc.id,
                        document_name: doc.name,
                        page: 1,
                        text: '',
                      });
                    }
                  }
                }}
                maxVisible={3}
                alwaysExpanded={true}
              />
            </div>
          )}
          {/* Timestamp + context toggle + actions — on hover */}
          <div
            className={`mt-1 pr-1 flex items-center justify-end gap-3 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150`}
          >
            {message.timestamp && (
              <span
                className={`text-[10px] tabular-nums ${theme.colors.text.secondary} opacity-60`}
              >
                {message.timestamp.toLocaleTimeString('es-CL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {hasContext && (
              <button
                onClick={() => onToggleExpansion('context', message.id)}
                className={`text-[10px] ${theme.colors.text.secondary} opacity-60 hover:opacity-100 transition-opacity`}
              >
                {expandedContext.has(message.id) ? 'Ocultar docs' : 'Ver docs'}
              </button>
            )}
            <MessageActions
              message={message}
              onCopy={() => onCopy(message.markdown ?? message.text)}
              onSave={() => onSave(message.id)}
              onEdit={onEdit ? () => onEdit(message.id) : undefined}
              onDelete={() => onDelete(message.id)}
            />
          </div>
        </>
      )}
    </div>
  );

  function renderUserMessage() {
    const knownDocNames =
      selectedMinimalDocuments
        ?.map((doc) => (doc.name || doc.original_filename || '').replace(/@/g, '').trim())
        .filter(Boolean) || [];

    const mentions = detectMentions(message.text, knownDocNames);

    if (mentions.length === 0) {
      return <span>{message.text}</span>;
    }

    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    mentions.forEach((mention, index) => {
      // Añadir texto antes de la mención
      if (mention.startIndex > currentIndex) {
        const beforeText = message.text.substring(currentIndex, mention.startIndex);
        if (beforeText) {
          parts.push(<span key={`text-before-${index}`}>{beforeText}</span>);
        }
      }

      // Añadir el chip de mención
      parts.push(
        <MentionChip
          key={`mention-${index}-${mention.startIndex}`}
          type={mention.type as any}
          value={mention.value}
        />
      );

      currentIndex = mention.endIndex;
    });

    // Añadir texto después de la última mención
    if (currentIndex < message.text.length) {
      const afterText = message.text.substring(currentIndex);
      if (afterText) {
        parts.push(<span key="text-after-last">{afterText}</span>);
      }
    }

    return <span>{parts}</span>;
  }

  function renderAIMessage() {
    return (
      <MarkdownRenderer
        content={message.markdown ?? message.text}
        remarkPlugins={[remarkCitations(message.citations, message.isStreaming)]}
        components={createCitationComponents(message)}
      />
    );
  }

  function renderAIFooter() {
    const sourceCount = message.citations ? Object.keys(message.citations).length : 0;
    const statsExpanded = expandedUsageStats.has(message.id);
    const autoInfo = message.autoSelectionInfo;

    // Confidence label — only show when there are actual citations (not on fallback responses)
    let confLabel: string | null = null;
    let confColorClass = '';
    if (message.confidence != null && sourceCount > 0) {
      if (message.confidence >= 0.7) {
        confLabel = 'Alta';
        confColorClass = 'text-emerald-500';
      } else if (message.confidence >= 0.4) {
        confLabel = 'Media';
        confColorClass = 'text-amber-500';
      } else {
        confLabel = 'Baja';
        confColorClass = 'text-red-400';
      }
    }

    // Hide entire footer while streaming — show only after response is complete
    if (message.isStreaming) return null;

    return (
      <>
        {/* Unified meta-line: metadata left + actions right (hover). flex-wrap for mobile. */}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 animate-fade-in">
          {/* Left: timestamp · fuentes · confidence · ver detalles */}
          <div
            className={`flex items-center gap-1.5 text-xs ${theme.colors.text.secondary} min-w-0`}
          >
            {message.timestamp && (
              <span className="tabular-nums flex-shrink-0">
                {message.timestamp.toLocaleTimeString('es-CL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {sourceCount > 0 && (
              <>
                <span className="opacity-30">·</span>
                <span className="text-blue-400/80 flex-shrink-0">
                  {sourceCount} fuente{sourceCount > 1 ? 's' : ''}
                </span>
              </>
            )}
            {confLabel && (
              <>
                <span className="opacity-30">·</span>
                <span className={`flex-shrink-0 ${confColorClass}`}>{confLabel}</span>
              </>
            )}
            {autoInfo && autoInfo.document_names.length > 0 && (
              <>
                <span className="opacity-30">·</span>
                <span
                  className="flex-shrink-0 text-nb-accent/70 cursor-help"
                  title={`Documentos auto-seleccionados: ${autoInfo.document_names.join(', ')}`}
                >
                  <Zap className="w-3 h-3 inline mr-0.5" />
                  {autoInfo.document_names.length} doc
                  {autoInfo.document_names.length !== 1 ? 's' : ''} auto
                </span>
              </>
            )}
            {isAdmin && (
              <>
                <span className="opacity-30">·</span>
                <button
                  onClick={() => onToggleExpansion('stats', message.id)}
                  className={`flex-shrink-0 p-0.5 rounded hover:${theme.colors.bg.tertiary} hover:${theme.colors.text.primary} transition-colors`}
                  title={statsExpanded ? 'Ocultar detalles' : 'Ver detalles'}
                >
                  <BarChart2
                    className={`w-3 h-3 ${statsExpanded ? theme.colors.text.primary : ''}`}
                  />
                </button>
              </>
            )}
          </div>

          {/* Right: actions (appear on hover) */}
          <div className="opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150 flex-shrink-0">
            <MessageActions
              message={message}
              onCopy={() => onCopy(message.markdown ?? message.text)}
              onSave={() => onSave(message.id)}
              onDelete={() => onDelete(message.id)}
              onRate={
                onRate ? (rating, feedback) => onRate(message.id, rating, feedback) : undefined
              }
              onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
            />
          </div>
        </div>

        {/* References — always visible when citations exist */}
        {message.citations && Object.keys(message.citations).length > 0 && (
          <ChatReferences
            messageId={message.id}
            citations={message.citations}
            isExpanded={true}
            onToggle={() => {}}
            onCitationClick={onCitationClick}
          />
        )}

        {/* Expanded admin stats */}
        {isAdmin && statsExpanded && (
          <div className="mt-1">
            <UsageStats
              stats={message.usageStats}
              ragStats={message.ragStats}
              citations={message.citations}
              onCitationClick={onCitationClick}
              aiFlowTrace={message.aiFlowTrace}
              timestamp={message.timestamp}
              isCollapsed={false}
              hideHeader={true}
              onToggleCollapse={() => onToggleExpansion('stats', message.id)}
            />
          </div>
        )}
      </>
    );
  }
}, areMessageItemPropsEqual);

export default MessageItem;
